// Adicionados tipos de componente v2: number, switch, simpletable, table
// Adicionados tipos de componente webhook v5: date, select, querySelect, keyValue, keyValueObject, tag, search, divider, section
// Adicionados tipos de componente beta: button, datagrid, image
export type BuilderComponentType = 'tabs' | 'panel' | 'text' | 'textarea' | 'number' | 'switch' | 'simpletable' | 'table' | 'date' | 'select' | 'querySelect' | 'keyValue' | 'keyValueObject' | 'tag' | 'search' | 'divider' | 'section' | 'button' | 'spreadsheet' | 'datagrid' | 'row' | 'image' | 'dropdown';

// Interfaces da Grade V3
export interface GridPosition {
  row: number;
  col: number;
  rowSpan: number;
  colSpan: number;
}

export interface GridTemplate {
  columns: number;
  rows: number;
  gap: string;
}

export interface BaseProps {
  label?: string; // chave i18n
  placeholder?: string; // chave i18n
  rowSpan?: number; // linhas da grade ocupadas no layout da UI
  colSpan?: number; // colunas da grade ocupadas no layout da UI
  
  // Propriedades opcionais da grade V3 (auto-calculadas)
  gridRow?: number; // Posição da linha da grade auto-calculada
  gridCol?: number; // Posição da coluna da grade auto-calculada
  
  // NOVO: Propriedades de posicionamento na grade (armazenadas)
  col?: number; // Posição da coluna na grade (0-indexed)
  row?: number; // Posição da linha na grade (0-indexed)
  
  // Data binding properties - Sistema de injeção de dados
  dataPath?: string;        // JSON path para binding de dados (ex: "customer.name", "items[0].price")
  dataTransform?: string;   // Função de transformação opcional (ex: "uppercase", "currency", "date")
  
  // Event scripting - Sistema de eventos e comportamentos
  events?: import('./event-script.models').EventConfig[]; // Eventos configurados (onChange, onInit)
}

export interface TextProps extends BaseProps {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  backgroundColor?: string;
  textColor?: string;
  entityName?: string;
  fieldName?: string;
  defaultValue?: string;
  dependencies?: SearchFieldDependency[];
  fieldMappings?: SearchFieldMapping[];
}

export interface TextAreaProps extends BaseProps {
  required?: boolean;
  minRows?: number;
  backgroundColor?: string;
  textColor?: string;
  entityName?: string;
  fieldName?: string;
  defaultValue?: string;
  dependencies?: SearchFieldDependency[];
  fieldMappings?: SearchFieldMapping[];
}

// Version 2 component prop interfaces
export interface NumberProps extends BaseProps {
  required?: boolean;
  min?: number;
  max?: number;
  step?: number;
  precision?: number;
  backgroundColor?: string;
  textColor?: string;
  entityName?: string;
  fieldName?: string;
  defaultValue?: number;
  decimalPlaces?: number;
  formatThousands?: boolean;
  thousandSeparator?: '.' | ',';
  decimalSeparator?: '.' | ',';
  addonBefore?: string;
  addonAfter?: string;
  dependencies?: SearchFieldDependency[];
  fieldMappings?: SearchFieldMapping[];
}

export interface SwitchProps extends BaseProps {
  defaultValue?: boolean;
  checkedChildren?: string;
  unCheckedChildren?: string;
  backgroundColor?: string;
  textColor?: string;
  entityName?: string;
  fieldName?: string;
  tabControl?: TabControlConfig;
  dependencies?: SearchFieldDependency[];
  fieldMappings?: SearchFieldMapping[];
}

// Interface para configuração de controle de abas
export interface TabControlConfig {
  enabled: boolean;              // Se o controle de abas está ativo
  onTrueAction: 'show' | 'hide'; // Ação quando switch = true
  onFalseAction: 'show' | 'hide'; // Ação quando switch = false
  targetTabs: string[];          // IDs das abas a serem controladas
  mode: 'include' | 'exclude';   // Incluir apenas as targetTabs ou todas exceto elas
}

// Interface para informações de aba
export interface TabInfo {
  id: string;
  name: string;
  disabled?: boolean;
}

export interface TableColumn {
  key: string;
  label: string; // i18n key
  type?: 'text' | 'number' | 'boolean' | 'date';
  sortable?: boolean;
  filterable?: boolean;
}

export interface TableConfigurationField {
  field: string;
  label: string;
  placeholder?: string;
  type: string;
  colspan: number;
  noLabel: boolean;
  required: boolean;
  isDisabled?: boolean;
  noFormItemMarginBottom?: boolean;
  showFormula?: boolean;
  options?: any[];
}

export interface TableConfiguration {
  showAdd: boolean;
  showRemove: boolean;
  fields: TableConfigurationField[];
}

export interface SimpleTableProps extends BaseProps {
  dataSource?: ReadonlyArray<Record<string, unknown>>;
  columns?: ReadonlyArray<TableColumn>;
  tableConfiguration?: TableConfiguration;
  headerBackgroundColor?: string;
  headerTextColor?: string;
  rowBackgroundColor?: string;
  rowTextColor?: string;
  alternateRowColor?: string;
  borderColor?: string;
  hoverColor?: string;
}

export interface TableProps extends BaseProps {
  dataSource?: ReadonlyArray<Record<string, unknown>>;
  columns?: ReadonlyArray<TableColumn>;
  sorting?: boolean;
  filtering?: boolean;
  pagination?: boolean;
  headerBackgroundColor?: string;
  headerTextColor?: string;
  rowBackgroundColor?: string;
  rowTextColor?: string;
  alternateRowColor?: string;
  borderColor?: string;
  hoverColor?: string;
}

export interface TabItemProps {
  id: string;
  name: string; // i18n key
  icon?: string;
  themeIcon?: string;
  twotoneColor?: string;
  disabled?: boolean;
}

// Configuração de campo na barra de contexto das abas
export interface ContextFieldConfig {
  label: string;       // Label do campo (ex: "SKU", "Título")
  dataPath: string;    // Path do dado no item selecionado (ex: "sku", "title")
  sourceType?: string; // Tipo do componente de origem ('search', 'querySelect', etc.)
  searchProperties?: { // Metadados da pesquisa (apenas para campos search/querySelect)
    entityName: string;
    fields: {
      keyField: string;
      valueField: string;
    };
  };
}

export interface TabsProps extends BaseProps {
  items: TabItemProps[];
  contextFields?: ContextFieldConfig[]; // Campos exibidos na barra de contexto
  backgroundColor?: string; // Fundo das abas individuais
  textColor?: string; // Texto das abas individuais
  activeTabColor?: string; // Fundo da aba ativa
  activeTabTextColor?: string; // Texto da aba ativa
  borderColor?: string; // Borda das abas individuais
  contentBackgroundColor?: string; // Fundo da área de conteúdo
  contentTextColor?: string; // Texto da área de conteúdo
  hoverTabColor?: string; // Cor de hover das abas
  // NOVO: Propriedades do container principal das tabs (nz-tabset)
  tabsContainerBackgroundColor?: string; // Fundo do container principal
  tabsContainerBorderColor?: string; // Borda do container principal
  tabsContainerBorderRadius?: string; // Raio da borda do container
  tabsContainerPadding?: string; // Padding do container
  // NOVO: Propriedades do wrapper externo (.canvas-node)
  wrapperBackgroundColor?: string; // Fundo do wrapper externo
  wrapperBorderColor?: string; // Borda do wrapper externo
  wrapperBorderRadius?: string; // Raio da borda do wrapper
  wrapperPadding?: string; // Padding do wrapper
}

// V5 Webhook component props interfaces
export interface DateProps extends BaseProps {
  dateFormat?: string;
  showTime?: boolean;
  required?: boolean;
  defaultValue?: string;
  dependencies?: SearchFieldDependency[];
  fieldMappings?: SearchFieldMapping[];
}

export interface SelectProps extends BaseProps {
  options?: Array<{ value: any; label: string }>;
  multiple?: boolean;
  showSearch?: boolean;
  required?: boolean;
}

// NOVO: Componente Dropdown (paleta básica) - Menu suspenso com opções configuráveis
export interface DropdownProps extends BaseProps {
  options?: Array<{ value: any; label: string; isDefault?: boolean }>;
  multiple?: boolean;
  showSearch?: boolean;
  required?: boolean;
  backgroundColor?: string;
  textColor?: string;
  entityName?: string;
  fieldName?: string;
  defaultValue?: any;
  dependencies?: SearchFieldDependency[];
  fieldMappings?: SearchFieldMapping[];
}

export interface QuerySelectProps extends BaseProps {
  condition?: string;
  showKey?: boolean;
  entityName?: string;
  required?: boolean;
}

export interface KeyValueProps extends BaseProps {
  autocompleteKey?: string[];
  autocompleteValue?: string[];
  required?: boolean;
}

export interface KeyValueObjectProps extends BaseProps {
  required?: boolean;
}

export interface TagProps extends BaseProps {
  options?: Array<unknown>;
  required?: boolean;
}

// Dependência entre campos: controla habilitação e, opcionalmente, filtra pesquisa
export interface SearchFieldDependency {
  sourceDataPath: string;    // dataPath do campo-pai (ex: "CODECOMPANY")
  sourceLabel: string;       // Label para mensagem (ex: "Empresa")
  filterColumn?: string;     // Coluna de filtro na entidade (opcional). Quando presente, constrói condition SQL. Quando ausente, só controla enable/disable.
  requiredMessage?: string;  // Mensagem customizada (ex: "Selecione a empresa primeiro")
  clearOnChange?: boolean;   // Limpar valor quando dependência muda (default: true)
}

// Mapeamento de campo: propaga valores para outros campos do formulário.
// Para search: sourceColumn extrai coluna da linha do resultado.
// Para outros campos: sourceColumn é opcional — quando ausente, usa o valor próprio do campo.
export interface SearchFieldMapping {
  sourceColumn?: string;   // Coluna da entidade (search only). Se ausente, usa valor do campo.
  targetDataPath: string;  // dataPath do campo destino no form (ex: "CODECONTRACT")
  targetLabel?: string;    // Label para exibição na config (ex: "Contrato")
}

export interface SearchProps extends BaseProps {
  showKey?: boolean;
  searchProperties?: {
    entityName: string;
    fields: {
      keyField: string;
      valueField: string;
    };
  };
  required?: boolean;
  dependencies?: SearchFieldDependency[]; // Dependências em outros campos de pesquisa
  fieldMappings?: SearchFieldMapping[];   // Campos derivados: preencher outros campos ao selecionar
}

export interface DividerProps extends BaseProps {
  dashed?: boolean;
  type?: "horizontal" | "vertical";
  orientation?: "left" | "center" | "right";
  color?: string;
}

export interface SectionProps extends BaseProps {
  title?: string;
  collapsible?: boolean;
  collapsed?: boolean;
}

// Panel component props (for webhook structure)
export interface PanelProps extends BaseProps {
  name: string;
  active?: boolean;
  disabled?: boolean;
  order?: number;
  collapsible?: boolean;
  collapsed?: boolean;
  backgroundColor?: string; // NOVO: Cor de fundo do painel (obsoleto - usar panelContentBackgroundColor)
  textColor?: string; // NOVO: Cor do texto do painel (obsoleto - usar panelHeaderTextColor)
  // NOVO: Propriedades de estilo específicas do painel
  panelBorderColor?: string; // Cor da borda do painel
  panelHeaderBackgroundColor?: string; // Cor de fundo do cabeçalho
  panelHeaderTextColor?: string; // Cor do texto do cabeçalho
  panelContentBackgroundColor?: string; // Cor de fundo do conteúdo
  // NOVO: Propriedades de posicionamento na grade (herdadas de BaseProps, mas explicitadas aqui para clareza)
  // colSpan, rowSpan, col, row já estão em BaseProps via herança
}

// Beta Button component props
export interface ButtonProps extends BaseProps {
  text?: string;
  buttonType?: 'primary' | 'default' | 'dashed' | 'text' | 'link' | 'danger';
  size?: 'small' | 'default' | 'large';
  disabled?: boolean;
  loading?: boolean;
  icon?: string;
  backgroundColor?: string;
  textColor?: string;
  // Ações do botão
  action?: string; // Tipo de ação genérica (ex: 'executeFlow', 'save', 'cancel', 'custom')
  actionType?: 'save' | 'cancel' | 'custom' | 'flow' | 'executeFlow'; // Compatibilidade
  flowId?: number; // ID do workflow a ser executado
  // Configuração de fluxo MKTPCFG
  flowConfig?: {
    idConf?: number;           // ID da configuração MKTPCFG
    confName?: string;         // Nome da configuração (para exibição)
    showCodeCompany?: boolean; // Se deve mostrar seletor de empresa
    codeCompany?: number;      // Empresa fixa (opcional)
    newMode?: boolean;         // true = motor NOVO (NEWMODE/ta-flow, async); false/undefined = motor ANTIGO
  };
  // Modo de envio de dados ao fluxo
  flowDataMode?: 'full' | 'selectedItem'; // 'full' = JSON completo (padrão), 'selectedItem' = item selecionado
}

// Image component props
export interface ImageProps extends BaseProps {
  imageUrl?: string;        // URL estática da imagem (http/https ou data URI base64)
  dataPath?: string;        // Path JSON para URL dinâmica (pode retornar URL ou base64)
  alt?: string;             // Texto alternativo para acessibilidade
  width?: number;           // Largura em pixels
  height?: number;          // Altura em pixels
  borderRadius?: number;    // Borda arredondada em pixels
  tooltip?: string;         // Tooltip ao passar o mouse
  enableLightbox?: boolean; // Abrir imagem em lightbox ao clicar
}

// Spreadsheet component props
export interface SpreadsheetProps extends BaseProps {
  // Configurações básicas
  showToolbar?: boolean;
  showFormulaBar?: boolean;
  showContextMenu?: boolean;
  readonly?: boolean;

  // Dados da planilha
  data?: SpreadsheetData;

  // Configurações visuais
  backgroundColor?: string;
  borderColor?: string;
  headerBackgroundColor?: string;
  headerTextColor?: string;
  gridColor?: string;

  // Configurações de tamanho
  defaultRowHeight?: number;
  defaultColumnWidth?: number;
  maxRows?: number;
  maxColumns?: number;

  // Funcionalidades
  allowImportExport?: boolean;
  enableFormulas?: boolean;
  enableCharts?: boolean;

  // Validação
  dataValidation?: SpreadsheetValidation[];
}

// Estrutura de dados da planilha
export interface SpreadsheetData {
  sheets: SpreadsheetSheet[];
  activeSheetIndex: number;
}

export interface SpreadsheetSheet {
  name: string;
  data: SpreadsheetCell[][];
  formulas: Record<string, string>;
  styles: Record<string, SpreadsheetCellStyle>;
  merges: SpreadsheetMerge[];
  rowHeights: Record<number, number>;
  columnWidths: Record<number, number>;
}

export interface SpreadsheetCell {
  value?: string | number | boolean;
  formula?: string;
  style?: string;
  type?: "text" | "number" | "boolean" | "date" | "formula";
}

export interface SpreadsheetCellStyle {
  backgroundColor?: string;
  textColor?: string;
  fontSize?: number;
  fontWeight?: "normal" | "bold";
  fontStyle?: "normal" | "italic";
  textAlign?: "left" | "center" | "right";
  verticalAlign?: "top" | "middle" | "bottom";
  border?: SpreadsheetBorder;
  format?: string;
}

export interface SpreadsheetBorder {
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
}

export interface SpreadsheetMerge {
  startRow: number;
  startCol: number;
  endRow: number;
  endCol: number;
}

export interface SpreadsheetValidation {
  range: string;
  type: "list" | "number" | "date" | "custom";
  criteria: any;
  errorMessage?: string;
}

// DataGrid component props - Sistema de injeção de dados
export interface DataGridColumn {
  field: string;             // Nome do campo
  header: string;            // Label da coluna
  type: 'text' | 'number' | 'date' | 'boolean' | 'search' | 'dropdown';
  dataPath: string;          // JSON path específico da coluna (ex: "name", "details.price")
  width?: string;            // Largura da coluna (ex: "100px", "20%")
  sortable?: boolean;        // Se a coluna é ordenável
  filterable?: boolean;      // Se a coluna é filtrável
  editable?: boolean;        // Se a coluna permite edição inline
  formatter?: string;        // Função de formatação (ex: "currency", "date")
  // Propriedades para tipo 'search' (busca em entidade server-side)
  searchProperties?: {
    entityName: string;
    fields: { keyField: string; valueField: string };
  };
  showKey?: boolean;
  fieldMappings?: SearchFieldMapping[];
  // Propriedades para tipo 'dropdown' (opções estáticas)
  options?: Array<{ value: any; label: string; isDefault?: boolean }>;
  multiple?: boolean;
  showSearch?: boolean;
}

export interface DataGridProps extends BaseProps {
  label?: string;                 // NOVO: Label customizável da grade (ex: "Lista de Itens")
  columns: DataGridColumn[];      // Colunas da grade
  dataPath?: string;              // JSON path para o array de dados (ex: "data.products")
  enablePagination?: boolean;     // Habilitar paginação
  pageSize?: number;              // Tamanho da página
  maxVisibleRows?: number;        // Linhas visíveis antes do scroll vertical (0 = sem limite)
  enableFilters?: boolean;        // Habilitar filtros
  enableSorting?: boolean;        // Habilitar ordenação
  enableCRUD?: boolean;           // Habilitar operações CRUD
  selectionMode?: 'single' | 'multiple' | 'none'; // Modo de seleção
  // Master-Detail
  enableMasterDetail?: boolean;   // Habilitar conexão com formulário
  masterDetailFormPanel?: string; // ID do panel com os campos do formulário (legacy: single panel)
  masterDetailFormPanels?: string[]; // IDs dos panels conectados (multi-panel support)
  masterDetailAutoGenerate?: boolean; // Auto-gerar campos do formulário
  autoAddFirstRow?: boolean;     // Adicionar primeira linha automaticamente ao abrir a tela sem dados
  autoAddOrder?: number;         // Ordem de execução do auto-add (menor = primeiro; ex: 0=principal, 1=filha)
  viewMode?: 'inline' | 'drawer' | 'flip' | 'spreadsheet'; // Modo de visualização grid/form (default: 'inline')
  drawerWidth?: number;          // Largura do drawer em % (default: 40) - usado quando viewMode='drawer'
}

// NOVO: Row (Linha horizontal para campos lado a lado)
export interface RowProps extends BaseProps {
  gap?: string;                   // Espaçamento entre campos (ex: "12px")
  alignItems?: 'start' | 'center' | 'end' | 'stretch'; // Alinhamento vertical
  justifyContent?: 'start' | 'center' | 'end' | 'space-between' | 'space-around'; // Alinhamento horizontal
  backgroundColor?: string;       // Cor de fundo da linha
  padding?: string;               // Espaçamento interno
  order?: number;                 // Ordem de renderização
}

export type BuilderProps = TextProps | TextAreaProps | TabsProps | PanelProps | NumberProps | SwitchProps | SimpleTableProps | TableProps | DateProps | SelectProps | DropdownProps | QuerySelectProps | KeyValueProps | KeyValueObjectProps | TagProps | SearchProps | DividerProps | SectionProps | ButtonProps | SpreadsheetProps | DataGridProps | RowProps;

export interface BuilderNode<TProps extends BuilderProps = BuilderProps> {
  id: string;
  type: BuilderComponentType;
  props: TProps;
  children?: BuilderNode[]; // reserved
  parentId?: string | null; // parent tabs node id when nested
  tabId?: string | null; // tab id under a tabs node when nested
  updateKey?: number; // NOVO: Key para forçar re-render do componente quando props mudam
}

// Output JSON alignment (subset focused on v1 types)
export interface OutputTab {
  id: string;
  name: string;
  icon?: string;
  themeIcon?: string;
  twotoneColor?: string;
  disabled?: boolean;
  colors?: Record<string, string>; // Color properties from tabs component
}

export interface OutputPanel {
  id: string;
  name: string;
  active: boolean;
  disabled: boolean;
  tab: string; // OutputTab.id
  order?: number; // NOVO: Ordem do painel
  collapsible?: boolean; // NOVO: Se pode ser colapsado
  collapsed?: boolean; // NOVO: Se está colapsado
  backgroundColor?: string; // NOVO: Cor de fundo do painel
  textColor?: string; // NOVO: Cor do texto do painel
}

export type OutputFieldType = 'text' | 'textarea' | 'number' | 'switch' | 'select' | 'tag' | 'date' | 'querySelect' | 'keyValue' | 'keyValueObject' | 'search' | 'divider' | 'section' | 'button' | 'simpletable' | 'table' | 'spreadsheet' | 'datagrid' | 'image';

export interface OutputFieldBase {
  field: string;
  type: OutputFieldType;
  label: string;
  placeholder?: string;
  panel: string; // OutputPanel.id
  col: number;
  row: number;
  colSpan: number;
  required?: boolean;
  colors?: Record<string, string>; // Color properties from all components
  // Propriedades de mapeamento de banco de dados
  entityName?: string;
  fieldName?: string;
  // Data binding properties - Sistema de injeção de dados
  dataPath?: string;        // JSON path para binding de dados
  dataTransform?: string;   // Função de transformação opcional
}

export interface OutputTextField extends OutputFieldBase {
  type: 'text';
  minLength?: number;
  maxLength?: number;
}

export interface OutputTextAreaField extends OutputFieldBase {
  type: 'textarea';
  minRows?: number;
}

export interface OutputNumberField extends OutputFieldBase {
  type: 'number';
  min?: number;
  max?: number;
  step?: number;
  precision?: number;
}

export interface OutputSwitchField extends OutputFieldBase {
  type: 'switch';
  defaultValue?: boolean;
  checkedChildren?: string;
  unCheckedChildren?: string;
}

export interface OutputButtonField extends OutputFieldBase {
  type: 'button';
  text?: string;
  buttonType?: 'primary' | 'default' | 'dashed' | 'text' | 'link' | 'danger';
  size?: 'small' | 'default' | 'large';
  icon?: string;
  actionType?: 'save' | 'cancel' | 'custom';
  disabled?: boolean;
  loading?: boolean;
}

export interface OutputSelectField extends OutputFieldBase {
  type: 'select';
  options?: ReadonlyArray<{ value: unknown; label: string }>;
  multiple?: boolean;
}

export interface OutputTagField extends OutputFieldBase {
  type: 'tag';
  options?: ReadonlyArray<unknown>;
}

// V5 Webhook output field interfaces
export interface OutputDateField extends OutputFieldBase {
  type: 'date';
  dateFormat?: string;
  showTime?: boolean;
}

export interface OutputQuerySelectField extends OutputFieldBase {
  type: 'querySelect';
  condition?: string;
  showKey?: boolean;
  entityName?: string;
}

export interface OutputKeyValueField extends OutputFieldBase {
  type: 'keyValue';
  autocompleteKey?: string[];
  autocompleteValue?: string[];
}

export interface OutputKeyValueObjectField extends OutputFieldBase {
  type: 'keyValueObject';
}

export interface OutputSearchField extends OutputFieldBase {
  type: 'search';
  entityName?: string;
  keyField?: string;
  valueField?: string;
  showKey?: boolean;
}

export interface OutputDividerField extends OutputFieldBase {
  type: 'divider';
  dashed?: boolean;
  dividerType?: "horizontal" | "vertical";
  orientation?: "left" | "center" | "right";
  color?: string;
}

export interface OutputSectionField extends OutputFieldBase {
  type: 'section';
  title?: string;
  collapsible?: boolean;
  collapsed?: boolean;
}

export interface OutputSimpleTableField extends OutputFieldBase {
  type: 'simpletable';
  columns?: ReadonlyArray<{
    key: string;
    label: string;
    type: string;
    sortable?: boolean;
    filterable?: boolean;
  }>;
  tableConfiguration?: {
    showAdd?: boolean;
    showRemove?: boolean;
    fields: ReadonlyArray<{
      field: string;
      label: string;
      placeholder?: string;
      type: string;
      colspan?: number;
      noLabel?: boolean;
      required?: boolean;
      isDisabled?: boolean;
      noFormItemMarginBottom?: boolean;
      showFormula?: boolean;
      options?: ReadonlyArray<unknown>;
    }>;
  };
}

export interface OutputTableField extends OutputFieldBase {
  type: 'table';
  columns?: ReadonlyArray<{
    key: string;
    label: string;
    type: string;
    sortable?: boolean;
    filterable?: boolean;
  }>;
  sorting?: boolean;
  filtering?: boolean;
  pagination?: boolean;
}

export interface OutputSpreadsheetField extends OutputFieldBase {
  type: 'spreadsheet';
  showToolbar?: boolean;
  showFormulaBar?: boolean;
  showContextMenu?: boolean;
  readonly?: boolean;
  backgroundColor?: string;
  borderColor?: string;
  headerBackgroundColor?: string;
  headerTextColor?: string;
  gridColor?: string;
  defaultRowHeight?: number;
  defaultColumnWidth?: number;
  maxRows?: number;
  maxColumns?: number;
  allowImportExport?: boolean;
  enableFormulas?: boolean;
  enableCharts?: boolean;
}

export interface OutputDataGridField extends OutputFieldBase {
  type: 'datagrid';
  columns: Array<{
    field: string;
    header: string;
    type: 'text' | 'number' | 'date' | 'boolean' | 'search' | 'dropdown';
    dataPath: string;
    width?: string;
    sortable?: boolean;
    filterable?: boolean;
    editable?: boolean;
    formatter?: string;
    searchProperties?: {
      entityName: string;
      fields: { keyField: string; valueField: string };
    };
    showKey?: boolean;
    fieldMappings?: SearchFieldMapping[];
    options?: Array<{ value: any; label: string; isDefault?: boolean }>;
    multiple?: boolean;
    showSearch?: boolean;
  }>;
  enablePagination?: boolean;
  pageSize?: number;
  enableFilters?: boolean;
  enableSorting?: boolean;
  enableCRUD?: boolean;
  selectionMode?: 'single' | 'multiple' | 'none';
}

export interface OutputImageField extends OutputFieldBase {
  type: 'image';
  imageUrl?: string;
  alt?: string;
  width?: number;
  height?: number;
  borderRadius?: number;
  enableLightbox?: boolean;
}

export type OutputField = OutputTextField | OutputTextAreaField | OutputNumberField | OutputSwitchField | OutputButtonField | OutputSelectField | OutputTagField | OutputDateField | OutputQuerySelectField | OutputKeyValueField | OutputKeyValueObjectField | OutputSearchField | OutputDividerField | OutputSectionField | OutputSimpleTableField | OutputTableField | OutputSpreadsheetField | OutputDataGridField | OutputImageField;

export interface BuilderJsonOutput {
  tabs: OutputTab[];
  panels: OutputPanel[];
  fields: OutputField[];
}

// V3 Grid metadata (optional)
export interface GridMetadata {
  position: { row: number; col: number };
  autoPlaced: boolean;
}

// V5 Palette category system
export interface PaletteCategory {
  id: string;
  labelKey: string;
  descriptionKey: string;
  items: PaletteItem[];
}

export interface PaletteItem {
  type: BuilderComponentType;
  labelKey: string;
  descriptionKey: string;
}

// Validation system
export enum ValidationErrorType {
  NO_PANEL_PARENT = 'NO_PANEL_PARENT',
  INVALID_CONTAINER_NESTING = 'INVALID_CONTAINER_NESTING',
  MISSING_BASE_STRUCTURE = 'MISSING_BASE_STRUCTURE'
}

export interface ValidationResult {
  isValid: boolean;
  errorType?: ValidationErrorType;
  errorMessage?: string;
  suggestedAction?: string;
}

// V5 Enhanced JSON output with webhook support
export interface EnhancedOutputField extends OutputFieldBase {
  // Webhook-specific properties
  showFormula?: boolean;
  autocomplete?: string[];
  formula?: string;
  condition?: string;
  searchProperties?: {
    entityName: string;
    fields: { keyField: string; valueField: string };
  };
  divider?: {
    dashed?: boolean;
    type?: string;
    orientation?: string;
    color?: string;
  };
  _webhook?: {
    showFormula?: boolean;
    formula?: string;
    validation?: {
      pattern?: string;
      message?: string;
    };
  };
}


